import React, { useId } from "react";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import {
  COLOR_CONSTANTS,
  TYPOGRAPHY_SCALE,
} from "../../../../constants/styles";

/**
 * 비활성 버튼의 커서. 버튼 자신(`getContainerStyles`)과, 사유 툴팁이 붙어 pointer-events 를 끈 버튼 대신 커서를 세우는
 * span(`reasonHoverTarget`)이 함께 쓴다. 로딩 중인 버튼 자신은 `getContainerStyles` 의 로딩 분기가 `"progress"` 로 덮는다.
 */
const DISABLED_CURSOR = "not-allowed";

export type SidebarItemButtonMode = "off" | "on" | "disabled";

export type SidebarItemButtonProps = {
  icon: {
    component: React.ReactNode;
    name: string;
  };
  mode?: SidebarItemButtonMode;
  onClick?: () => void;
  disabled?: boolean;
  /** true 면 아이콘을 스피너로 바꾸고 클릭을 차단한다 — 로딩 중 잠금 표시용 */
  loading?: boolean;
  /**
   * 비활성인 **까닭** — 비활성일 때만 밝힌다. 포인터를 올리면 툴팁으로 뜨고, 스크린리더는 버튼의 접근 설명으로 읽는다.
   * 버튼은 네이티브 `disabled` 그대로라 키보드 포커스 순서는 사유가 없을 때와 같다. 주지 않거나 활성이면 종전과 같이 그린다.
   */
  disabledReason?: string;
};

export function SidebarItemButton({
  icon,
  mode = "off",
  onClick,
  disabled = false,
  loading = false,
  disabledReason,
}: SidebarItemButtonProps) {
  const isDisabled = disabled || loading || mode === "disabled";
  const isActive = mode === "on";
  /** 지금 밝힐 사유 — 활성인 버튼에는 사유가 와도 달지 않는다. */
  const shownReason = isDisabled ? disabledReason : undefined;
  const reasonId = useId();

  const getContainerStyles = () => {
    const baseStyles = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      width: 40,
      height: 40,
      cursor: isDisabled ? DISABLED_CURSOR : "pointer",
      borderRadius: 1,
      transition: "all 0.2s ease",
      position: "relative" as const,
      border: "none",
      background: "transparent",
      padding: 0,
    };

    if (loading) {
      return {
        ...baseStyles,
        cursor: "progress",
        opacity: 0.7,
      };
    }

    if (isDisabled) {
      return {
        ...baseStyles,
        opacity: 0.38,
      };
    }

    if (isActive) {
      return {
        ...baseStyles,
        backgroundColor: COLOR_CONSTANTS.primary._states.hover,
        "&:hover": {
          backgroundColor: COLOR_CONSTANTS.primary._states.focus,
        },
      };
    }

    return {
      ...baseStyles,
      "&:hover": {
        backgroundColor: COLOR_CONSTANTS.primary._states.hover,
      },
    };
  };

  const getIconContainerStyles = () => {
    const baseStyles = {
      padding: "0 4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      gap: "3px",
      width: "100%",
      height: "100%",
      borderRadius: 1,
    };

    if (isActive) {
      return {
        ...baseStyles,
        backgroundColor: COLOR_CONSTANTS.primary._states.focus,
        "& img": {
          filter: `brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(7491%) hue-rotate(217deg) brightness(91%) contrast(101%)`, // #1D74FF
        },
      };
    }

    return baseStyles;
  };

  const button = (
    <Box
      component="button"
      onClick={isDisabled ? undefined : onClick}
      sx={getContainerStyles()}
      disabled={isDisabled}
      aria-describedby={shownReason ? reasonId : undefined}
    >
      <Box sx={getIconContainerStyles()}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {loading ? (
            <CircularProgress size={18} thickness={4} />
          ) : (
            icon.component
          )}
        </Box>

        <Typography
          variant="caption"
          sx={{
            ...TYPOGRAPHY_SCALE.tooltipLabel,
            color: COLOR_CONSTANTS.text.secondary,
            lineHeight: 1,
            fontWeight: 400,
          }}
        >
          {icon.name}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {shownReason ? (
        <>
          {/*
            `describeChild` — 기본값이면 MUI 가 사유를 감싸는 span 의 `aria-label` 로 싣는다(generic 요소에 이름). 사유는
            아래 버튼의 접근 설명 한 길로만 전한다.
          */}
          <Tooltip title={shownReason} placement="right" arrow describeChild>
            <Box component="span" sx={reasonHoverTarget}>
              {button}
            </Box>
          </Tooltip>
          {/*
            버튼의 `aria-describedby` 가 가리키는 사유. `hidden` 이어도 직접 참조한 내용은 접근 설명으로 계산되고, 탐색 모드에서
            같은 문장을 두 번 읽지 않는다. 버튼 안에 두지 않는 것은 접근 **이름**에 섞이지 않게 하려는 것이다.
          */}
          <span id={reasonId} hidden>
            {shownReason}
          </span>
        </>
      ) : (
        button
      )}
      {isActive && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            width: 4,
            height: 24,
            backgroundColor: COLOR_CONSTANTS.primary.main,
            borderRadius: "0 4px 4px 0",
          }}
        />
      )}
    </Box>
  );
}

/**
 * 사유 툴팁을 여는 자리. 비활성 `<button>` 은 포인터 이벤트를 받지 않고 브라우저에 따라 삼키기도 한다 — 그래서 hover 는
 * 이 span 이 받고, 버튼은 이벤트를 흘려보낸다. 버튼이 이벤트를 흘려보내면 제 커서도 서지 않으므로 커서를 이 자리로 옮긴다.
 */
const reasonHoverTarget = {
  display: "inline-flex",
  cursor: DISABLED_CURSOR,
  "& > button": { pointerEvents: "none" },
} as const;
